import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import QuotationPreviewModal from '../components/QuotationPreviewModal';
import { 
    Pencil, 
    ArrowLeft, 
    Building2, 
    Save, 
    Loader2 
} from 'lucide-react';
import './CreateProfessionalQuotation.css';

const EditProfessionalQuotation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [formData, setFormData] = useState(null);

    useEffect(() => {
        fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchQuotation = async () => {
        try {
            const response = await api.get(`/professional-quotations/${id}`);
            const quot = response.data.quotation;

            // Set form data from quotation
            setFormData({
                quotationDate: new Date(quot.quotationDate).toISOString().split('T')[0],
                projectDetails: quot.projectDetails,
                clientDetails: quot.clientDetails,
                designerDetails: quot.designerDetails,
                workStages: quot.workStages,
                charges: quot.charges || { stages: [], additionalNotes: '' },
                inceptionOfWork: quot.inceptionOfWork || { totalFees: 0, perSqftRate: 0, notes: '' },
                generalAccord: quot.generalAccord && quot.generalAccord.length > 0 ? quot.generalAccord : [''],
                designServices: quot.designServices && quot.designServices.length > 0 ? quot.designServices : [''],
                closeOutPeriod: quot.closeOutPeriod || '',
                closeOutMonths: quot.closeOutMonths || 0
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleWorkStageChange = (index, field, value) => {
        const newWorkStages = [...formData.workStages];
        newWorkStages[index][field] = value;
        setFormData(prev => ({ ...prev, workStages: newWorkStages }));
    };

    const addWorkStage = () => {
        setFormData(prev => ({
            ...prev,
            workStages: [
                ...prev.workStages,
                {
                    stageNumber: prev.workStages.length + 1,
                    stageName: '',
                    description: ''
                }
            ]
        }));
    };

    const removeWorkStage = (index) => {
        if (formData.workStages.length > 1) {
            const newWorkStages = formData.workStages.filter((_, i) => i !== index);
            newWorkStages.forEach((stage, i) => {
                stage.stageNumber = i + 1;
            });
            setFormData(prev => ({ ...prev, workStages: newWorkStages }));
        }
    };

    const handleChargeStageChange = (index, field, value) => {
        const newChargeStages = [...formData.charges.stages];
        newChargeStages[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
        setFormData(prev => ({
            ...prev,
            charges: { ...prev.charges, stages: newChargeStages }
        }));
    };

    const addChargeStage = () => {
        setFormData(prev => ({
            ...prev,
            charges: {
                ...prev.charges,
                stages: [
                    ...prev.charges.stages,
                    { stageName: '', amount: 0, percentage: '' }
                ]
            }
        }));
    };

    const removeChargeStage = (index) => {
        const newChargeStages = formData.charges.stages.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            charges: { ...prev.charges, stages: newChargeStages }
        }));
    };

    const handleArrayChange = (field, index, value) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: [...prev[field], '']
        }));
    };

    const removeArrayItem = (field, index) => {
        if (formData[field].length > 1) {
            const newArray = formData[field].filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, [field]: newArray }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError('');

            const cleanedFormData = {
                ...formData,
                generalAccord: formData.generalAccord.filter(item => item.trim() !== ''),
                designServices: formData.designServices.filter(item => item.trim() !== '')
            };

            const response = await api.put(`/professional-quotations/${id}`, cleanedFormData);

            if (response.data.success) {
                setSuccess('Project Assurance updated successfully! Opening preview...');
                setTimeout(() => {
                    setSaving(false);
                    setSuccess(''); // Clear success message before opening modal
                    setShowPreviewModal(true);
                }, 1000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update Project Assurance');
            setSaving(false);
        }
    };

    if (loading) return <Loading />;
    if (!formData) return <Alert type="error" message="Quotation not found" />;

    const handleModalClose = (saved) => {
        setShowPreviewModal(false);
        navigate(`/professional-quotations/${id}`);
    };

    return (
        <div className="create-professional-quotation-container">
            {showPreviewModal && (
                <QuotationPreviewModal
                    quotationId={id}
                    onClose={handleModalClose}
                />
            )}

            <div className="page-header" style={{ marginBottom: '30px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Pencil size={28} /> Edit Project Assurance
                    </h1>
                    <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                        Update quotation details
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/professional-quotations/${id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <ArrowLeft size={18} /> Back to View
                </button>
            </div>

            {error && <Alert type="error" message={error} onClose={() => setError('')} />}
            {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

            {/* Render the same form as CreateProfessionalQuotation but with submit as update */}
            <form onSubmit={handleSubmit}>
                {/* All form sections - same as Create page */}
                {/* For brevity, I'm showing one section. The complete code will have all sections */}

                {/* Project Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Building2 size={20} className="anim-pulse" /> Project Information
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Project Type <span className="required">*</span></label>
                                <select
                                    value={formData.projectDetails.projectType}
                                    onChange={(e) => handleChange('projectDetails', 'projectType', e.target.value)}
                                    className="form-control"
                                    required
                                >
                                    <option value="Residential">Residential</option>
                                    <option value="Hospital">Hospital</option>
                                    <option value="Commercial">Commercial</option>
                                    <option value="Office">Office</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Project Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={formData.projectDetails.projectName}
                                    onChange={(e) => handleChange('projectDetails', 'projectName', e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Project Location <span className="required">*</span></label>
                            <input
                                type="text"
                                value={formData.projectDetails.projectLocation}
                                onChange={(e) => handleChange('projectDetails', 'projectLocation', e.target.value)}
                                className="form-control"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Physical Address <span className="required">*</span></label>
                            <textarea
                                value={formData.projectDetails.projectAddress}
                                onChange={(e) => handleChange('projectDetails', 'projectAddress', e.target.value)}
                                className="form-control"
                                rows="2"
                                required
                            />
                        </div>
                    </div>
                </div>

                
                {/* 1.2 Client Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">Client Information</div>
                    <div className="card-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Client Name</label>
                                <input type="text" className="form-control" value={formData.clientDetails.name} onChange={(e) => handleChange('clientDetails', 'name', e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Cell Phone</label>
                                <input type="text" className="form-control" value={formData.clientDetails.cellPhone} onChange={(e) => handleChange('clientDetails', 'cellPhone', e.target.value)} required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" value={formData.clientDetails.email} onChange={(e) => handleChange('clientDetails', 'email', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Physical Address</label>
                                <textarea className="form-control" rows="2" value={formData.clientDetails.physicalAddress} onChange={(e) => handleChange('clientDetails', 'physicalAddress', e.target.value)} required />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 1.3 Designer Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">Designer / Firm Information</div>
                    <div className="card-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Firm Name</label>
                                <input type="text" className="form-control" value={formData.designerDetails.firmName} onChange={(e) => handleChange('designerDetails', 'firmName', e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Head Designer</label>
                                <input type="text" className="form-control" value={formData.designerDetails.headDesignerName} onChange={(e) => handleChange('designerDetails', 'headDesignerName', e.target.value)} required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" value={formData.designerDetails.email} onChange={(e) => handleChange('designerDetails', 'email', e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Cell Phone</label>
                                <input type="text" className="form-control" value={formData.designerDetails.cellPhone} onChange={(e) => handleChange('designerDetails', 'cellPhone', e.target.value)} required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">License Number</label>
                                <input type="text" className="form-control" value={formData.designerDetails.licenseNumber} onChange={(e) => handleChange('designerDetails', 'licenseNumber', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Physical Address</label>
                                <textarea className="form-control" rows="2" value={formData.designerDetails.physicalAddress} onChange={(e) => handleChange('designerDetails', 'physicalAddress', e.target.value)} required />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2.2 Work Stages */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Work Stages</span>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addWorkStage}>+ Add Stage</button>
                    </div>
                    <div className="card-body">
                        {formData.workStages.map((stage, index) => (
                            <div key={index} style={{ marginBottom: '15px', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <strong>Stage {stage.stageNumber}</strong>
                                    {formData.workStages.length > 1 && (
                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeWorkStage(index)}>Remove</button>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Stage Name</label>
                                    <input type="text" className="form-control" value={stage.stageName} onChange={(e) => handleWorkStageChange(index, 'stageName', e.target.value)} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description (Optional)</label>
                                    <textarea className="form-control" rows="2" value={stage.description} onChange={(e) => handleWorkStageChange(index, 'description', e.target.value)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 2.3 Inception Fees */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">Inception of Work - Fees</div>
                    <div className="card-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Total Fees (₹)</label>
                                <input type="number" className="form-control" value={formData.inceptionOfWork.totalFees} onChange={(e) => handleChange('inceptionOfWork', 'totalFees', parseFloat(e.target.value) || 0)} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Per Sqft Rate (₹)</label>
                                <input type="number" className="form-control" value={formData.inceptionOfWork.perSqftRate} onChange={(e) => handleChange('inceptionOfWork', 'perSqftRate', parseFloat(e.target.value) || 0)} required />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Notes</label>
                            <textarea className="form-control" rows="3" value={formData.inceptionOfWork.notes} onChange={(e) => handleChange('inceptionOfWork', 'notes', e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* 3. General Accord */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>General Accord</span>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => addArrayItem('generalAccord')}>+ Add Point</button>
                    </div>
                    <div className="card-body">
                        {formData.generalAccord.map((item, index) => (
                            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input type="text" className="form-control" value={item} onChange={(e) => handleArrayChange('generalAccord', index, e.target.value)} required />
                                {formData.generalAccord.length > 1 && (
                                    <button type="button" className="btn btn-danger" onClick={() => removeArrayItem('generalAccord', index)}>X</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. Design Services */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Design Services</span>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => addArrayItem('designServices')}>+ Add Service</button>
                    </div>
                    <div className="card-body">
                        {formData.designServices.map((item, index) => (
                            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input type="text" className="form-control" value={item} onChange={(e) => handleArrayChange('designServices', index, e.target.value)} required />
                                {formData.designServices.length > 1 && (
                                    <button type="button" className="btn btn-danger" onClick={() => removeArrayItem('designServices', index)}>X</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 5. Charges / Payment Stages */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Charges / Payment Stages</span>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addChargeStage}>+ Add Payment Stage</button>
                    </div>
                    <div className="card-body">
                        {formData.charges.stages.map((stage, index) => (
                            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <input type="text" className="form-control" placeholder="Stage Name" value={stage.stageName} onChange={(e) => handleChargeStageChange(index, 'stageName', e.target.value)} required />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <input type="number" className="form-control" placeholder="Amount (₹)" value={stage.amount} onChange={(e) => handleChargeStageChange(index, 'amount', e.target.value)} required />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <input type="text" className="form-control" placeholder="Percentage / Note" value={stage.percentage} onChange={(e) => handleChargeStageChange(index, 'percentage', e.target.value)} required />
                                </div>
                                <button type="button" className="btn btn-danger" onClick={() => removeChargeStage(index)}>X</button>
                            </div>
                        ))}
                        <div className="form-group" style={{ marginTop: '15px' }}>
                            <label className="form-label">Additional Notes</label>
                            <textarea className="form-control" rows="2" value={formData.charges.additionalNotes} onChange={(e) => handleChange('charges', 'additionalNotes', e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* 6. Close Out Period */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">Close Out Period</div>
                    <div className="card-body">
                        <div className="form-group">
                            <label className="form-label">Duration (Months)</label>
                            <input type="number" className="form-control" value={formData.closeOutMonths} onChange={(e) => setFormData(prev => ({ ...prev, closeOutMonths: parseFloat(e.target.value) || 0 }))} step="0.1" required />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Description text</label>
                            <textarea className="form-control" rows="3" value={formData.closeOutPeriod} onChange={(e) => setFormData(prev => ({ ...prev, closeOutPeriod: e.target.value }))} required />
                        </div>
                    </div>
                </div>


                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', padding: '20px 0' }}>
                    <button
                        type="button"
                        onClick={() => navigate(`/professional-quotations/${id}`)}
                        className="btn btn-secondary"
                        style={{ padding: '12px 35px' }}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={saving}
                        className="btn btn-primary"
                        style={{
                            padding: '12px 45px',
                            background: saving ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        {saving ? 'Updating...' : 'Update Project Assurance'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditProfessionalQuotation;
